/*
  ba-gift-only-guard.js
  WHAT: Blocks checkout while the cart contains nothing but $0 gift lines.
  WHY:  Owner 2026-10-06, testing on an Android phone: "i added an x4i and the mic-x was added
        correctly. but then i removed the x4i and the mic-x was not removed. it let me go to the
        check out with the free mic only in the cart."

        Reproduced and measured on the draft. Gift Box reacts to a cart change asynchronously,
        so removing the X4i leaves a window in which the gift is still there and the cart totals
        $0. Sampled every 400ms:

            t+0.3s   2 lines, $199.00
            t+1.0s   ORPHAN - 1 free line, $0.00
            t+1.7s   ORPHAN
            t+2.3s   ORPHAN
            t+3.1s   empty

        A 1.3s window on a fast desktop connection, and longer on a phone. Tap Checkout inside
        it and you reach checkout with a free $59 microphone and nothing paid for.

        The gift does get cleaned up - this is a race, not a stuck cart - so the fix is to stop
        the customer transacting DURING the window rather than to fight Gift Box over the line
        item. Removing the gift ourselves would mean two systems writing the same cart.

  WHEN: Added 2026-10-06.

  NOTE: This is a front-end guard and a determined visitor could still POST to /checkout. The
        durable fix is a Cart/Checkout Validation Shopify Function (the store is Plus, so it is
        available) rejecting a $0-total cart server-side. Raised with the owner; this ships
        first because it needs no app install and is reversible in one file.
*/
(function () {
  'use strict';

  var NOTE_ID = 'ba-gift-only-note';
  var MESSAGE = 'Add a product to your cart — a gift on its own can’t be checked out.';

  function checkoutButtons() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[name="checkout"], .cart__checkout')
    );
  }

  function apply(giftOnly) {
    checkoutButtons().forEach(function (btn) {
      if (giftOnly) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.dataset.baGiftOnly = '1';
        var wrap = btn.closest('.cart__checkout-wrapper') || btn.parentNode;
        if (wrap && !wrap.querySelector('#' + NOTE_ID)) {
          var p = document.createElement('p');
          p.id = NOTE_ID;
          p.className = 'ba-gift-only-note';
          /* polite, not assertive: the state changes while the cart is still settling and an
             assertive region would interrupt a screen reader mid-sentence. */
          p.setAttribute('role', 'status');
          p.textContent = MESSAGE;
          wrap.appendChild(p);
        }
      } else if (btn.dataset.baGiftOnly) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
        delete btn.dataset.baGiftOnly;
      }
    });
    if (!giftOnly) {
      var note = document.getElementById(NOTE_ID);
      if (note && note.parentNode) note.parentNode.removeChild(note);
    }
  }

  /* Pre-emptive hold.
     Polling alone leaves a gap: measured t+1.5s cart already gift-only, checkout still enabled,
     because the sweep had not come round yet. Any cart mutation therefore disables checkout
     IMMEDIATELY and evaluate() decides within a few hundred ms whether to release it.

     Deliberately no explanatory note on the hold - the cart is mid-change and in most cases it
     releases before anyone reads anything. The note is only added once the gift-only state is
     confirmed. Checking out while the cart is still settling is not a thing we want anyway. */
  function hold() {
    checkoutButtons().forEach(function (btn) {
      if (!btn.hasAttribute('disabled')) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.baGiftOnly = '1';
      }
    });
  }

  function evaluate() {
    /* Ask the cart, never the DOM: the drawer and the cart page render differently and the
       whole point is that the markup may be a step behind the real cart. */
    return fetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        apply(cart.item_count > 0 && cart.total_price === 0);
      })
      .catch(function () { /* leave the button as rendered rather than block on a failure */ });
  }

  /* Re-check after anything that can change the cart. The window closes on its own in a couple
     of seconds, so a short follow-up sweep catches both the open and the close without polling
     forever. */
  function sweep() {
    evaluate();
    var n = 0;
    var t = setInterval(function () {
      evaluate();
      if (++n >= 8) clearInterval(t);
    }, 700);
  }

  document.addEventListener('DOMContentLoaded', sweep);
  ['cart:updated', 'cart:build', 'cart:refresh', 'ajaxProduct:added']
    .forEach(function (ev) { document.addEventListener(ev, sweep); });

  /* Impulse and several apps mutate the cart through fetch without emitting an event, so also
     watch for cart writes directly. */
  if (window.fetch) {
    var orig = window.fetch;
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (/\/cart\/(change|update|clear)/.test(url)) {
        /* A removal is the dangerous one: hold before the request even resolves. */
        hold();
        p.then(function () { sweep(); }).catch(function () { evaluate(); });
      } else if (/\/cart\/add/.test(url)) {
        /* Adding cannot create a gift-only cart on its own, so no hold - just re-check. */
        p.then(function () { sweep(); }).catch(function () {});
      }
      return p;
    };
  }
})();
